import type { ReactElement } from 'react';
import React from 'react';
import clsx from 'clsx';
import type { BoxWhiskerData } from 'feature-components/charts/box-whisker/types';
import 'feature-components/report-parts/chart-overview-table/chart-overview-table.scss';

type Props = {
  threshold: number;
  isNationalVisible: boolean;
  className?: string;
  data: (BoxWhiskerData & {
    numberOfStudents: number;
    numberOfFinishedResponses: number;
    mean: number;
    std: number;
  })[];

  referenceData:
    | (BoxWhiskerData & {
        std: number;
        mean: number;
      })[]
    | undefined;
};

export default function YearLevelSummary({
  className,
  threshold = 0,
  data = [],
  referenceData = [],
  isNationalVisible
}: Props): ReactElement {
  return (
    <table className="chart-overview-table">
      <thead>
        <tr className={clsx('report-header text-end', className)}>
          <th className="text-start ps-2">Year Level (ref year)</th>
          <th>Students (Completed)</th>
          <th key="3">Mean Scale Score</th>
          <th>Standard Deviation</th>
          <th>Highest Score</th>
          <th>Upper Quartile</th>
          <th>Median</th>
          <th>Lower Quartile</th>
          <th className="pe-2">Lowest Score</th>
        </tr>
      </thead>
      <tbody>
        {data.map((d) => {
          const reference = referenceData.find(({ id }) => id === d?.id);
          return (
            <tr key={d.id} className="text-end">
              <td className="text-start ps-2">{d.label}</td>
              <td>
                {d.numberOfFinishedResponses}/{d.numberOfResponses}
              </td>
              {(d.numberOfResponses || 0) >= threshold ? (
                <>
                  <td>
                    {d.mean.toFixed(1)}
                    {isNationalVisible && reference && ` (${reference.mean.toFixed(1)})`}
                  </td>
                  <td>
                    {d.std.toFixed(1)}
                    {isNationalVisible && reference && ` (${reference.std.toFixed(1)})`}
                  </td>
                  <td>
                    {d.maximum.toFixed(1)}
                    {isNationalVisible && reference && ` (${reference?.maximum.toFixed(1)})`}
                  </td>
                  <td>
                    {d.upperQuartile.toFixed(1)}
                    {isNationalVisible && reference && `(${reference?.upperQuartile.toFixed(1)})`}
                  </td>
                  <td>
                    {d.median.toFixed(1)}
                    {isNationalVisible && reference && ` (${reference?.median.toFixed(1)})`}
                  </td>
                  <td>
                    {d.lowerQuartile.toFixed(1)}
                    {isNationalVisible && reference && `(${reference?.lowerQuartile.toFixed(1)})`}
                  </td>
                  <td className="pe-2">
                    {d.minimum.toFixed(1)}
                    {isNationalVisible && reference && ` (${reference?.minimum.toFixed(1)})`}
                  </td>
                </>
              ) : (
                <td className="text-center" colSpan={7}>
                  Number of responses below threshold of {threshold}
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
